import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Container,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";

import AdatisLogo from "../../../Components/Common/AdatisLogo";
import SignaturePad from "../../../Components/Studio/SignaturePad";
import {
  cancelPublicAppointment,
  confirmPublicAppointment,
  getPublicAppointment,
  getPublicPreattend,
  PublicAppointmentManage,
  PublicPreattendForm,
  submitPublicPreattendAnswers,
  submitPublicPreattendConsent,
} from "../../../services/publicAppointmentManageService";
import {
  collectConsentText,
  parseSchemaFields,
  ParsedSchemaField,
} from "../../../services/clientRecordService";

/**
 * Reagendar deve permanecer no mesmo host do painel.
 * A API monta URL absoluta via FRONTEND_URL — se o .env de prod estiver
 * com localhost, o link quebrava. Preferimos o path `/book/...`.
 */
function toSameOriginBookPath(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.startsWith("/book/")) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // keep original
  }

  return url;
}

type Step = "home" | "health" | "consent";

const PublicAppointmentManagePage: React.FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);

  const [appointment, setAppointment] = useState<PublicAppointmentManage | null>(null);
  const [preattend, setPreattend] = useState<PublicPreattendForm | null>(null);
  const [step, setStep] = useState<Step>("home");
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [signature, setSignature] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  document.title = t("public_appointment.pageTitle");

  const schemaFields = useMemo(
    () => parseSchemaFields(preattend?.template?.schema_json),
    [preattend?.template?.schema_json]
  );

  const healthFields = useMemo(
    () =>
      schemaFields.filter(
        (field) =>
          !["section", "consent_text", "consent_checkbox", "signature"].includes(field.type)
      ),
    [schemaFields]
  );

  const consentFields = useMemo(
    () =>
      schemaFields.filter((field) =>
        ["consent_text", "consent_checkbox", "signature"].includes(field.type)
      ),
    [schemaFields]
  );

  const clearDeepLinkAction = useCallback(() => {
    if (!searchParams.has("action")) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const applyPreattend = useCallback((data: PublicPreattendForm | null) => {
    setPreattend(data);
    if (!data) {
      return;
    }

    const nextAnswers: Record<string, unknown> = { ...(data.answers_json ?? {}) };
    setAnswers(nextAnswers);
    setSignature(null);

    if (data.preattend_complete) {
      setStep("home");
      return;
    }

    if (data.needs_health_record) {
      setStep("health");
      return;
    }

    if (data.needs_consent) {
      setStep("consent");
    }
  }, []);

  const loadPreattend = useCallback(async (): Promise<PublicPreattendForm | null> => {
    if (!token) {
      return null;
    }

    try {
      const response = await getPublicPreattend(token);
      const data = response.data ?? null;
      applyPreattend(data);
      return data;
    } catch {
      setError(t("public_appointment.preattend.errors.load"));
      return null;
    }
  }, [applyPreattend, t, token]);

  const load = useCallback(async (): Promise<PublicAppointmentManage | null> => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);
    setNotFound(false);
    setExpired(false);

    try {
      const response = await getPublicAppointment(token);
      const data = response.data ?? null;
      setAppointment(data);
      return data;
    } catch (err) {
      const apiError = err as { code?: string; status?: number };

      if (apiError.code === "PUBLIC_APPOINTMENT_TOKEN_EXPIRED") {
        setExpired(true);
        return null;
      }

      if (
        apiError.code === "PUBLIC_APPOINTMENT_TOKEN_INVALID" ||
        apiError.code === "NOT_FOUND"
      ) {
        setNotFound(true);
        return null;
      }

      setError(t("public_appointment.errors.load"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  const runAction = useCallback(async (action: "confirm" | "cancel"): Promise<void> => {
    if (!token) {
      return;
    }

    setActing(true);
    setError(null);
    setFeedback(null);

    try {
      const response =
        action === "confirm"
          ? await confirmPublicAppointment(token)
          : await cancelPublicAppointment(token);

      setAppointment(response.data ?? null);
      setFeedback(
        action === "confirm"
          ? t("public_appointment.feedback.confirmed")
          : t("public_appointment.feedback.canceled")
      );
    } catch {
      setError(t("public_appointment.errors.action"));
    } finally {
      setActing(false);
    }
  }, [token, t]);

  const openPreattend = useCallback(async () => {
    setActing(true);
    setError(null);
    setFeedback(null);
    try {
      await loadPreattend();
    } finally {
      setActing(false);
    }
  }, [loadPreattend]);

  const setAnswer = (field: ParsedSchemaField, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [field.name]: value }));
  };

  const submitHealth = async () => {
    if (!token) {
      return;
    }

    setActing(true);
    setError(null);
    setFeedback(null);

    try {
      const healthAnswers: Record<string, unknown> = {};
      healthFields.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(answers, field.name)) {
          healthAnswers[field.name] = answers[field.name];
        }
      });

      const response = await submitPublicPreattendAnswers(token, healthAnswers);
      const data = response.data ?? null;
      applyPreattend(data);
      setFeedback(t("public_appointment.preattend.feedback.answersSaved"));

      if (data?.needs_consent) {
        setStep("consent");
      } else {
        setStep("home");
        await load();
      }
    } catch {
      setError(t("public_appointment.preattend.errors.saveAnswers"));
    } finally {
      setActing(false);
    }
  };

  const submitConsent = async () => {
    if (!token || !preattend) {
      return;
    }

    setActing(true);
    setError(null);
    setFeedback(null);

    try {
      const consentAnswers: Record<string, unknown> = {};
      consentFields
        .filter((field) => field.type === "consent_checkbox")
        .forEach((field) => {
          consentAnswers[field.name] = Boolean(answers[field.name]);
        });

      const snapshot =
        preattend.consent_text ||
        collectConsentText(schemaFields) ||
        undefined;

      const response = await submitPublicPreattendConsent(token, {
        answers_json: consentAnswers,
        consent_text_snapshot: snapshot,
        signature_image_base64: signature,
      });
      applyPreattend(response.data ?? null);
      setFeedback(t("public_appointment.preattend.feedback.consentSaved"));
      setStep("home");
      await load();
    } catch {
      setError(t("public_appointment.preattend.errors.saveConsent"));
    } finally {
      setActing(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading || !appointment || deepLinkHandledRef.current) {
      return;
    }

    const action = (searchParams.get("action") || "").toLowerCase();

    if (!action || !["confirm", "cancel", "reschedule", "preattend"].includes(action)) {
      return;
    }

    deepLinkHandledRef.current = true;
    clearDeepLinkAction();

    if (action === "reschedule" && appointment.actions.reschedule_url) {
      window.location.assign(toSameOriginBookPath(appointment.actions.reschedule_url));
      return;
    }

    if (action === "preattend" && appointment.actions.can_preattend) {
      void openPreattend();
      return;
    }

    if (action === "confirm" && appointment.actions.can_confirm) {
      void runAction("confirm");
      return;
    }

    if (action === "cancel" && appointment.actions.can_cancel) {
      void runAction("cancel");
    }
  }, [appointment, clearDeepLinkAction, loading, openPreattend, runAction, searchParams]);

  const renderField = (field: ParsedSchemaField) => {
    if (field.type === "section") {
      return (
        <h6 key={field.name} className="mt-3 mb-2">
          {field.label}
        </h6>
      );
    }

    if (field.type === "consent_text") {
      return (
        <Alert key={field.name} color="light" className="border mb-3">
          <div style={{ whiteSpace: "pre-wrap" }}>{field.body || field.label}</div>
        </Alert>
      );
    }

    if (field.type === "boolean" || field.type === "consent_checkbox") {
      return (
        <FormGroup key={field.name} check className="mb-3">
          <Input
            id={`preattend-${field.name}`}
            type="checkbox"
            checked={Boolean(answers[field.name])}
            disabled={acting}
            onChange={(event) => setAnswer(field, event.target.checked)}
          />
          <Label check htmlFor={`preattend-${field.name}`}>
            {field.label}
            {field.required ? " *" : ""}
          </Label>
        </FormGroup>
      );
    }

    if (field.type === "select") {
      return (
        <FormGroup key={field.name} className="mb-3">
          <Label htmlFor={`preattend-${field.name}`}>
            {field.label}
            {field.required ? " *" : ""}
          </Label>
          <Input
            id={`preattend-${field.name}`}
            type="select"
            value={String(answers[field.name] ?? "")}
            disabled={acting}
            onChange={(event) => setAnswer(field, event.target.value)}
          >
            <option value="">{t("public_appointment.preattend.selectPlaceholder")}</option>
            {field.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Input>
        </FormGroup>
      );
    }

    if (field.type === "signature") {
      return (
        <FormGroup key={field.name} className="mb-3">
          <Label>
            {field.label}
            {field.required ? " *" : ""}
          </Label>
          <SignaturePad value={signature} onChange={setSignature} disabled={acting} />
        </FormGroup>
      );
    }

    const inputType = field.type === "textarea" ? "textarea" : "text";

    return (
      <FormGroup key={field.name} className="mb-3">
        <Label htmlFor={`preattend-${field.name}`}>
          {field.label}
          {field.required ? " *" : ""}
        </Label>
        <Input
          id={`preattend-${field.name}`}
          type={inputType}
          rows={field.type === "textarea" ? 4 : undefined}
          value={String(answers[field.name] ?? "")}
          disabled={acting}
          onChange={(event) => setAnswer(field, event.target.value)}
        />
      </FormGroup>
    );
  };

  const showPreattendCta =
    Boolean(appointment?.actions.can_preattend) ||
    Boolean(appointment?.preattend?.can_fill_health_record) ||
    Boolean(appointment?.preattend?.can_sign_consent);

  const showPreattendOk = Boolean(appointment?.preattend?.preattend_complete);

  return (
    <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
      <Container style={{ maxWidth: 520 }}>
        <div className="text-center mb-4">
          <AdatisLogo variant="light" height={28} linkTo={false} />
          <p className="text-white-50 mt-2 mb-0">{t("public_appointment.subtitle")}</p>
        </div>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="light" />
            <p className="text-white-50 mt-3 mb-0">{t("public_appointment.loading")}</p>
          </div>
        )}

        {!loading && (expired || notFound || (error && !appointment)) && (
          <div className="bg-white rounded-3 p-4 shadow-sm">
            {expired && (
              <Alert color="warning" className="mb-0">
                {t("public_appointment.errors.expired")}
              </Alert>
            )}
            {notFound && (
              <Alert color="danger" className="mb-0">
                {t("public_appointment.errors.notFound")}
              </Alert>
            )}
            {error && !appointment && !expired && !notFound && (
              <Alert color="danger" className="mb-0">
                {error}
              </Alert>
            )}
          </div>
        )}

        {!loading && appointment && step === "home" && (
          <div className="bg-white rounded-3 p-4 shadow-sm">
            {feedback && (
              <Alert color="success" className="mb-3">
                {feedback}
              </Alert>
            )}
            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <h4 className="mb-1">{appointment.tenant?.name || t("public_appointment.fallbackStudio")}</h4>
            <p className="text-muted mb-3">{t("public_appointment.heading")}</p>

            <p className="mb-1">
              <strong>{t("public_appointment.fields.when")}:</strong> {appointment.schedule_label}
            </p>
            {appointment.service && (
              <p className="mb-1">
                <strong>{t("public_appointment.fields.service")}:</strong> {appointment.service.name}
              </p>
            )}
            {appointment.professional && (
              <p className="mb-1">
                <strong>{t("public_appointment.fields.professional")}:</strong>{" "}
                {appointment.professional.name}
              </p>
            )}
            <p className="mb-4">
              <strong>{t("public_appointment.fields.status")}:</strong>{" "}
              {t(`public_appointment.status.${appointment.status}`, {
                defaultValue: appointment.status,
              })}
            </p>

            {showPreattendOk && (
              <Alert color="success" className="mb-3">
                {t("public_appointment.preattend.completeBanner")}
              </Alert>
            )}

            <div className="d-grid gap-2">
              {appointment.actions.can_confirm && (
                <Button
                  color="success"
                  disabled={acting}
                  onClick={() => void runAction("confirm")}
                >
                  {t("public_appointment.actions.confirm")}
                </Button>
              )}
              {showPreattendCta && (
                <Button
                  color="primary"
                  disabled={acting}
                  onClick={() => void openPreattend()}
                >
                  {appointment.preattend?.needs_consent && !appointment.preattend?.needs_health_record
                    ? t("public_appointment.actions.signConsent")
                    : t("public_appointment.actions.fillPreattend")}
                </Button>
              )}
              {appointment.actions.reschedule_url && (
                <Button
                  color="primary"
                  outline
                  tag="a"
                  href={toSameOriginBookPath(appointment.actions.reschedule_url)}
                >
                  {t("public_appointment.actions.reschedule")}
                </Button>
              )}
              {appointment.actions.can_cancel && (
                <Button
                  color="danger"
                  outline
                  disabled={acting}
                  onClick={() => void runAction("cancel")}
                >
                  {t("public_appointment.actions.cancel")}
                </Button>
              )}
              {appointment.actions.talk_to_salon_url && (
                <Button
                  color="secondary"
                  outline
                  tag="a"
                  href={appointment.actions.talk_to_salon_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("public_appointment.actions.talkToSalon")}
                </Button>
              )}
              {appointment.actions.calendar_url && (
                <Button
                  color="link"
                  tag="a"
                  href={appointment.actions.calendar_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("public_appointment.actions.addToCalendar")}
                </Button>
              )}
            </div>
          </div>
        )}

        {!loading && appointment && step !== "home" && (
          <div className="bg-white rounded-3 p-4 shadow-sm">
            {feedback && (
              <Alert color="success" className="mb-3">
                {feedback}
              </Alert>
            )}
            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <h4 className="mb-1">
              {step === "health"
                ? t("public_appointment.preattend.healthTitle")
                : t("public_appointment.preattend.consentTitle")}
            </h4>
            <p className="text-muted mb-3">
              {preattend?.client?.first_name
                ? t("public_appointment.preattend.greeting", {
                    name: preattend.client.first_name,
                  })
                : t("public_appointment.preattend.greetingGeneric")}
            </p>

            {preattend?.template_unavailable && (
              <Alert color="warning">
                {t("public_appointment.preattend.templateUnavailable")}
              </Alert>
            )}

            {step === "health" && !preattend?.template_unavailable && (
              <>
                {preattend?.template?.title && (
                  <p className="fw-semibold mb-3">{preattend.template.title}</p>
                )}
                {healthFields.map(renderField)}
                <div className="d-grid gap-2 mt-3">
                  <Button color="primary" disabled={acting} onClick={() => void submitHealth()}>
                    {acting ? <Spinner size="sm" /> : t("public_appointment.preattend.actions.saveHealth")}
                  </Button>
                  <Button color="link" disabled={acting} onClick={() => setStep("home")}>
                    {t("public_appointment.preattend.actions.back")}
                  </Button>
                </div>
              </>
            )}

            {step === "consent" && (
              <>
                {preattend?.consent_text ? (
                  <Alert color="light" className="border mb-3">
                    <div style={{ whiteSpace: "pre-wrap" }}>{preattend.consent_text}</div>
                  </Alert>
                ) : null}
                {consentFields.map(renderField)}
                <div className="d-grid gap-2 mt-3">
                  <Button color="primary" disabled={acting} onClick={() => void submitConsent()}>
                    {acting ? <Spinner size="sm" /> : t("public_appointment.preattend.actions.saveConsent")}
                  </Button>
                  <Button color="link" disabled={acting} onClick={() => setStep("home")}>
                    {t("public_appointment.preattend.actions.back")}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Container>
    </div>
  );
};

export default PublicAppointmentManagePage;
